/*
	Infrastructure.tsx
	Page affichant l'architecture système et les détails des serveurs
	Inclut l'identité de l'instance, les sections frontend, API, monitoring et connexions externes
	Les accordéons ont été remplacés par des sections plates pour plus de lisibilité
*/

// Import React
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faServer,
	faDatabase,
	faGaugeHigh,
	faAngleLeft,
	faCircle,
	faRotateRight,
	faImage,
} from '@fortawesome/free-solid-svg-icons';

// Import Layout
import PageHeader from 'layout/PageHeader';
import WidgetSection from 'layout/WidgetSection';
import WidgetHeader from 'layout/WidgetHeader';

// Import Communs
import { apiFetch } from 'common/services/apiFetch';
import { getApiBaseUrl } from 'common/services/apiBaseUrl';
import ExternalConnectionCrudCard from 'features/externalConnection/components/ExternalConnectionCrudCard';
import { useAuth } from 'common/context/AuthContext';
import { INSTANCE_PROFILE_LABEL, useInstance } from 'features/instance';

// Interface TypeScript pour les informations frontend
interface FrontendInfo {
	hostname: string;
	platform: string;
	userAgent: string;
}

// Interface TypeScript pour les informations de l'API backend
interface ApiInfo {
	url: string;
	status: 'online' | 'offline' | 'checking';
	latencyMs: number | null;
	checkedAt: Date | null;
}

// Fonction retournant les informations du navigateur/frontend
const getFrontendInfo = (): FrontendInfo => ({
	hostname: window.location.hostname,
	platform: navigator.platform,
	userAgent: navigator.userAgent,
});

// Fonction pingant l'API backend et retournant la latence en ms
// Utilise apiFetch avec le token ; un 403 (non autorisé) est considéré comme "online" car le serveur répond
const pingApi = async (
	token: string
): Promise<{ latencyMs: number | null; online: boolean }> => {
	const apiBaseUrl = getApiBaseUrl();
	const start = performance.now();
	try {
		await apiFetch(`${apiBaseUrl}/v2/users?page=1&limit=1`, {
			headers: { Authorization: `Bearer ${token}` },
		});
		const latencyMs = Math.round(performance.now() - start);
		return { latencyMs, online: true };
	} catch (error) {
		const latencyMs = Math.round(performance.now() - start);
		// Si une erreur réseau survient (ex: serveur inaccessible), latencyMs est null
		// Si apiFetch lève une erreur 403 (serveur répond mais refuse), le serveur est online
		const isNetworkError = error instanceof TypeError;
		return {
			latencyMs: isNetworkError ? null : latencyMs,
			online: !isNetworkError,
		};
	}
};

const InfrastructurePage: React.FC = () => {
	// Hooks de state
	const [frontendInfo, setFrontendInfo] = useState<FrontendInfo | null>(null);
	const [apiInfo, setApiInfo] = useState<ApiInfo>({
		url: getApiBaseUrl(),
		status: 'checking',
		latencyMs: null,
		checkedAt: null,
	});
	const navigate = useNavigate();
	const { isAdmin } = useAuth();
	const { branding, status: instanceStatus } = useInstance();

	// Lance un ping vers l'API et met à jour l'état du widget
	const handlePingApi = async () => {
		const token = localStorage.getItem('token');
		if (!token) return;
		setApiInfo((previous) => ({ ...previous, status: 'checking' }));
		const { latencyMs, online } = await pingApi(token);
		setApiInfo((previous) => ({
			...previous,
			status: online ? 'online' : 'offline',
			latencyMs,
			checkedAt: new Date(),
		}));
	};

	// Chargement des infos frontend au montage du composant + ping initial de l'API
	useEffect(() => {
		setFrontendInfo(getFrontendInfo());
		void handlePingApi();
	}, []); // eslint-disable-line

	// Rendu du composant
	return (
		<div className="min-h-screen bg-gray-100 p-6">
			{/* Barre de titre de la page */}
			<PageHeader
				icon={faServer}
				title="Infrastructure"
				subtitle="Architecture systeme et connexions externes"
			/>

			<div className="max-w-6xl mx-auto mt-6 space-y-6">
				{/* Section : identité de l'instance, en lecture seule */}
				<WidgetSection>
					<WidgetHeader icon={faImage} title="Configuration de l'instance" />
					{instanceStatus === 'loading' && (
						<p className="text-sm text-gray-500">Chargement…</p>
					)}
					{instanceStatus === 'error' && (
						<p className="text-sm text-red-600">
							{"Impossible de lire l'identité de l'instance."}
						</p>
					)}
					{branding && (
						<ul className="space-y-1 text-sm text-gray-700">
							<li>
								<strong>Nom :</strong> {branding.displayName}
							</li>
							<li>
								<strong>{"Profil d'environnement :"}</strong>{' '}
								{INSTANCE_PROFILE_LABEL[branding.profile]}
							</li>
						</ul>
					)}
					{isAdmin && (
						<button
							type="button"
							onClick={() => navigate('/app/settings/infrastructure/logo')}
							className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
						>
							Configurer le logo
						</button>
					)}
				</WidgetSection>

				{/* Section : Serveur Frontend */}
				<WidgetSection>
					<WidgetHeader icon={faServer} title="Serveur Frontend" />
					{frontendInfo ? (
						<ul className="space-y-1 text-sm text-gray-700">
							<li>
								<strong>Hostname :</strong> {frontendInfo.hostname}
							</li>
							<li>
								<strong>Plateforme :</strong> {frontendInfo.platform}
							</li>
							<li>
								<strong>User Agent :</strong> {frontendInfo.userAgent}
							</li>
						</ul>
					) : (
						<p className="text-sm text-gray-500">
							Chargement des informations frontend...
						</p>
					)}
				</WidgetSection>

				{/* Section : Serveur API */}
				<WidgetSection>
					<WidgetHeader
						icon={faDatabase}
						title="Serveur API"
						actions={
							<button
								onClick={() => void handlePingApi()}
								title="Rafraîchir"
								className="text-gray-400 hover:text-blue-600 transition"
							>
								<FontAwesomeIcon
									icon={faRotateRight}
									className={
										apiInfo.status === 'checking' ? 'animate-spin' : ''
									}
								/>
							</button>
						}
					/>
					<ul className="space-y-1 text-sm text-gray-700">
						<li>
							<strong>URL de base :</strong>{' '}
							<span className="font-mono text-gray-600">
								{apiInfo.url || '—'}
							</span>
						</li>
						<li className="flex items-center gap-2">
							<strong>Statut :</strong>
							{apiInfo.status === 'checking' && (
								<span className="text-gray-400 italic">
									Vérification en cours…
								</span>
							)}
							{apiInfo.status === 'online' && (
								<span className="flex items-center gap-1 text-green-600 font-semibold">
									<FontAwesomeIcon icon={faCircle} className="text-xs" />
									En ligne
								</span>
							)}
							{apiInfo.status === 'offline' && (
								<span className="flex items-center gap-1 text-red-600 font-semibold">
									<FontAwesomeIcon icon={faCircle} className="text-xs" />
									Hors ligne
								</span>
							)}
						</li>
						<li>
							<strong>Latence :</strong>{' '}
							{apiInfo.latencyMs !== null ? `${apiInfo.latencyMs} ms` : '—'}
						</li>
						<li>
							<strong>Dernière vérification :</strong>{' '}
							{apiInfo.checkedAt
								? apiInfo.checkedAt.toLocaleTimeString('fr-FR')
								: '—'}
						</li>
					</ul>
				</WidgetSection>

				{/* Section : Monitoring */}
				<WidgetSection>
					<WidgetHeader icon={faGaugeHigh} title="Monitoring" />
					<p className="text-sm text-gray-500">
						{
							"Cette section permettra de remonter l'état du serveur et les logs vers la plateforme de monitoring."
						}
					</p>
				</WidgetSection>

				{/* Section : Connexions externes — forceIcon pour afficher faGlobe même en taille lg */}
				<ExternalConnectionCrudCard size="lg" hideReturn forceIcon />

				{/* Bouton retour */}
				<div className="flex justify-end">
					<button
						onClick={() => navigate('/app/settings')}
						className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
					>
						<FontAwesomeIcon icon={faAngleLeft} />
						Retour
					</button>
				</div>
			</div>
		</div>
	);
};

// Export du composant
export default InfrastructurePage;
